// Gmail threads the mailbox sweep filed onto one project, rendered as a table
// that mirrors the DriveSyncedFilesTree shape: per-row ExtractionStatusButton that
// opens an ExtractedContentPanel, an OutOfDateIcon + Mark out-of-date / Mark fresh
// toggle, and tombstone rendering for excluded threads.
//
// ONE row per LOGICAL Gmail conversation. Gmail fragments a conversation across
// several physical threadIds; the backend re-threads them into one upload on the
// RFC822 header reference graph
// (docs/Y2026-Q3/design-gmail-logical-thread-grouping.md) and this tree renders that
// decision - it does NOT re-group on the client.
//
// Lock-step note: the row shape and affordances should track DriveSyncedFilesTree
// so the operator's mental model carries across the two surfaces. The one
// divergence is the kind column: always email_thread, a static badge rather than
// the Drive-style inline kind dropdown.
import { useState } from "react";

import {
  UploadsExtractedProvider,
  useSetUploadFreshness,
  useUploadExtracted,
} from "../api";
import {
  UploadsTreeEntry,
  useDismissUpload,
  useUploadsTree,
} from "../api/hooks/useDriveSync";
import {
  useExcludeGmailThread,
  useReinstateGmailThread,
} from "../api/hooks/useGmailThreads";
import { gmailThreadUrl } from "../utils/gmailLinks";
import { displayUploadName } from "../utils/uploadDisplayName";
import {
  EmailThreadAttachmentsToggle,
  EmailThreadAttachmentsRows,
  EmailThreadTypeCell,
} from "./EmailThreadAttachments";
import { ExtractedContentPanel } from "./ExtractedContentPanel";
import { ExtractionStatusButton } from "./ExtractionStatusButton";
import { OutOfDateIcon } from "./OutOfDateIcon";
import { ReextractLink } from "./ReextractLink";
import { removalReasonLabel } from "./removalReason";
import { dedupeRenderedSightings, SightingList } from "./SightingList";
import { PossibleOtherSiteBadge } from "./SiteWarningBadge";
import styles from "../routes/ProjectFilesRoute.module.css";
import { Chevron } from "./Chevron";

interface Props {
  projectId: string;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.round(h / 24);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  } catch {
    return iso;
  }
}

// "Last message sent" date for one row: the most recent message in the
// thread (``gmail_last_message_at``), falling back to ``last_pulled_at`` for
// legacy rows synced before that field existed.
function entryLastMessageIso(entry: UploadsTreeEntry): string | null {
  return entry.gmail_last_message_at ?? entry.last_pulled_at;
}

function entryLastMessageMs(entry: UploadsTreeEntry): number {
  const iso = entryLastMessageIso(entry);
  return iso ? new Date(iso).getTime() : 0;
}

// Column sort, shared by the active and Excluded tables.
type SortField = "subject" | "messages" | "lastMessage";
type SortDir = "asc" | "desc";

// Each row is one logical conversation, so the sort key is read straight off
// the entry - no per-group aggregation.
function entrySortKey(field: SortField, entry: UploadsTreeEntry): string | number {
  if (field === "subject") return displayUploadName(entry.original_name).toLowerCase();
  if (field === "messages") return entry.gmail_message_count ?? 0;
  return entryLastMessageMs(entry);
}

function compareEntries(
  a: UploadsTreeEntry,
  b: UploadsTreeEntry,
  field: SortField,
  dir: SortDir,
): number {
  const ka = entrySortKey(field, a);
  const kb = entrySortKey(field, b);
  const cmp =
    typeof ka === "string"
      ? ka.localeCompare(kb as string)
      : (ka as number) - (kb as number);
  return dir === "asc" ? cmp : -cmp;
}

// Clickable column header. The active column's chevron points up for ascending; inactive
// sortable columns show a faint chevron to advertise they're clickable.
const SortHeader = ({
  label,
  field,
  sortField,
  sortDir,
  onSort,
  align,
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
  align?: "left" | "right";
}) => {
  const active = sortField === field;
  return (
    <th
      className={styles.tdSmall}
      style={{ fontWeight: 500, textAlign: align ?? "left" }}
    >
      <span
        role="button"
        tabIndex={0}
        onClick={() => onSort(field)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSort(field);
          }
        }}
        style={{
          cursor: "pointer",
          userSelect: "none",
          display: "inline-flex",
          gap: 4,
          alignItems: "center",
        }}
        title={`Sort by ${label}`}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        <span style={{ fontSize: 10, color: active ? "#374151" : "#cbd5e1" }}>
          <Chevron open={active && sortDir === "asc"} />
        </span>
      </span>
    </th>
  );
};

const WITHHELD_LABEL: Record<string, string> = {
  personal:
    "Email withheld as personal mail. Its attachments were judged business documents, so they are kept",
  credential:
    "Email withheld because it contains a password or other secret the platform could not fully remove. Its attachments were judged business documents, so they are kept",
};

export const GmailSyncedFilesTree = ({ projectId }: Props) => {
  const treeQuery = useUploadsTree(projectId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Independent of the ExtractedContentPanel toggle: an operator often wants the
  // attachment manifest WITHOUT the (large) extracted-attributes panel, and vice versa.
  const [attachmentsExpanded, setAttachmentsExpanded] = useState<Set<string>>(
    new Set(),
  );
  // Starts expanded so excluded threads can be seen and Reinstated at a glance.
  const [excludedCollapsed, setExcludedCollapsed] = useState(false);
  const [sortField, setSortField] = useState<SortField>("lastMessage");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    // A-Z for subject, newest / most-messages first for the numeric columns.
    setSortDir(field === "subject" ? "asc" : "desc");
  };

  const toggleIn =
    (setter: (update: (prev: Set<string>) => Set<string>) => void) => (id: string) =>
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
  const toggleExpanded = toggleIn(setExpanded);
  const toggleAttachmentsExpanded = toggleIn(setAttachmentsExpanded);

  const gmailFiles = treeQuery.data?.gmail_sync_files ?? [];
  // Excluded (tombstone) threads get a section of their own. Dismissed rows are
  // gone: /uploads/tree drops them and dismiss is permanent.
  const active = gmailFiles.filter((e) => e.status !== "removed");
  const excluded = gmailFiles
    .filter((e) => e.status === "removed")
    .sort((a, b) => {
      const ad = a.removed_at ? new Date(a.removed_at).getTime() : 0;
      const bd = b.removed_at ? new Date(b.removed_at).getTime() : 0;
      return bd - ad;
    });
  // Every row's extraction status from ONE batched request: a per-row fetch on a
  // project of a few hundred threads exhausted the database's connections.
  const extractedIds = active.map((e) => e.upload_id);

  if (treeQuery.isLoading) {
    return (
      <p style={{ color: "#888", fontSize: 13, padding: 12 }}>
        Loading Gmail threads…
      </p>
    );
  }
  const withheldGroups = treeQuery.data?.withheld_thread_attachments ?? [];
  if (gmailFiles.length === 0 && withheldGroups.length === 0) {
    return (
      <p style={{ color: "#888", fontSize: 13, padding: 12 }}>
        No threads filed by the sweep yet.
      </p>
    );
  }

  // Failed thread extractions drive the Files-tab "N failed" badge but are otherwise
  // buried on a single row, so they get the Drive tree's chip. Active rows only, to
  // agree with the summary bar above, which skips tombstoned and quarantined rows.
  const failedCount = gmailFiles.filter(
    (e) =>
      e.status !== "removed" &&
      e.status !== "quarantined" &&
      e.extraction_status === "failed",
  ).length;

  const renderThreadRow = (entry: UploadsTreeEntry) => (
    <GmailThreadRow
      key={entry.upload_id}
      entry={entry}
      projectId={projectId}
      expanded={expanded.has(entry.upload_id)}
      onToggleExpand={() => toggleExpanded(entry.upload_id)}
      attachmentsExpanded={attachmentsExpanded.has(entry.upload_id)}
      onToggleAttachmentsExpanded={() =>
        toggleAttachmentsExpanded(entry.upload_id)
      }
    />
  );

  // Fixed layout + one colgroup for both tables so their columns line up.
  const colgroup = (
    <colgroup>
      <col style={{ width: 36 }} />
      <col />
      <col style={{ width: 180 }} />
      <col style={{ width: 130 }} />
      <col style={{ width: 220 }} />
    </colgroup>
  );
  const tableStyle = {
    width: "100%",
    tableLayout: "fixed" as const,
    borderCollapse: "collapse" as const,
    fontSize: 13,
  };

  return (
    <UploadsExtractedProvider uploadIds={extractedIds}>
    <div>
      {failedCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            marginBottom: 12,
            fontSize: 12,
            color: "#991b1b",
            background: "transparent",
            border: "1px solid #f87171",
            borderRadius: 6,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            ✗ {failedCount} thread{failedCount === 1 ? "" : "s"} failed extraction
          </span>
          <span style={{ color: "#b91c1c" }}>
            Find the row{failedCount === 1 ? "" : "s"} below and use Re-extract
            (in the Type column) to retry, or Exclude the thread.
          </span>
        </div>
      )}
      <section style={{ marginBottom: 20 }}>
        <table style={tableStyle}>
          {colgroup}
          <thead>
            <tr style={{ textAlign: "left", color: "#6b7280" }}>
              <th className={styles.td} />
              <SortHeader
                label="Subject"
                field="subject"
                sortField={sortField}
                sortDir={sortDir}
                onSort={toggleSort}
              />
              <SortHeader
                label="Thread"
                field="messages"
                sortField={sortField}
                sortDir={sortDir}
                onSort={toggleSort}
              />
              <SortHeader
                label="Last message"
                field="lastMessage"
                sortField={sortField}
                sortDir={sortDir}
                onSort={toggleSort}
              />
              <th
                className={styles.tdSmall}
                style={{ fontWeight: 500, textAlign: "right" }}
              />
            </tr>
          </thead>
          <tbody>
            {[...active]
              .sort((a, b) => compareEntries(a, b, sortField, sortDir))
              .map((entry) => renderThreadRow(entry))}
          </tbody>
        </table>
      </section>
      {withheldGroups.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", marginBottom: 6 }}>
            Attachments from withheld emails (
            {withheldGroups.reduce((n, g) => n + g.attachments.length, 0)})
          </div>
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 6px", maxWidth: 760 }}>
            These files arrived as attachments to emails the platform withholds: an
            email judged to be personal, or to contain a password or other secret the
            platform could not fully remove, is never shown here or read by the agents, and only
            its inbox owner can see it in their read log. The attachments were judged
            separately as business documents, so they are kept on the project and the
            agents read them like any other file. That is why they are not listed under
            an email above.
          </p>
          <table style={tableStyle}>
            {colgroup}
            <tbody>
              {withheldGroups.flatMap((group, i) => [
                <tr key={`withheld-${i}`}>
                  <td className={styles.td} />
                  <td className={styles.td} colSpan={4} style={{ color: "#6b7280" }}>
                    {WITHHELD_LABEL[group.withheld_class] ??
                      "Email withheld until it is judged for confidentiality. Its attachments were judged business documents, so they are kept"}
                    {" - "}
                    {group.attachments.length} attachment
                    {group.attachments.length === 1 ? "" : "s"}
                  </td>
                </tr>,
                <EmailThreadAttachmentsRows
                  key={`withheld-${i}-attachments`}
                  uploadId={null}
                  projectId={projectId}
                  colSpan={5}
                  nestedAttachments={group.attachments}
                />,
              ])}
            </tbody>
          </table>
        </section>
      )}
      {excluded.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setExcludedCollapsed((c) => !c)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setExcludedCollapsed((c) => !c);
              }
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: "#1f2937",
              marginBottom: 6,
              cursor: "pointer",
              userSelect: "none",
            }}
            title={
              excludedCollapsed
                ? "Show excluded threads"
                : "Hide excluded threads"
            }
            aria-expanded={!excludedCollapsed}
          >
            <span style={{ width: 12, display: "inline-block", flexShrink: 0 }}>
              <Chevron tree open={!excludedCollapsed} />
            </span>
            <span>Excluded ({excluded.length})</span>
          </div>
          {!excludedCollapsed && (
            <table style={tableStyle}>
              {colgroup}
              <tbody>{excluded.map((entry) => renderThreadRow(entry))}</tbody>
            </table>
          )}
        </section>
      )}
    </div>
    </UploadsExtractedProvider>
  );
};

interface RowProps {
  entry: UploadsTreeEntry;
  projectId: string;
  expanded: boolean;
  onToggleExpand: () => void;
  attachmentsExpanded: boolean;
  onToggleAttachmentsExpanded: () => void;
}

const GmailThreadRow = ({
  entry,
  projectId,
  expanded,
  onToggleExpand,
  attachmentsExpanded,
  onToggleAttachmentsExpanded,
}: RowProps) => {
  const isTombstone = entry.status === "removed";
  const reasonText = removalReasonLabel(entry.removal_reason);
  // Opens the inbox the sweep read this thread from.
  const url = gmailThreadUrl(entry.gmail_thread_id, entry.gmail_account_email);
  const setFreshness = useSetUploadFreshness(projectId);
  const dismiss = useDismissUpload();
  const excludeThread = useExcludeGmailThread();
  const reinstate = useReinstateGmailThread();
  // Mirrors Drive: trigger the extracted-facts query lazily so we
  // don't fetch every row's facts up front.
  useUploadExtracted(expanded ? entry.upload_id : null);
  const isOutOfDate =
    entry.marked_out_of_date || (entry.out_of_date_fields?.length ?? 0) > 0;

  const displaySubject = displayUploadName(entry.original_name);

  const nameNode = url && !isTombstone ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open this thread in Gmail"
      style={{ color: "#2563eb", textDecoration: "underline" }}
    >
      {displaySubject}
    </a>
  ) : (
    <span
      className={styles.fileName}
      style={
        isTombstone
          ? { textDecoration: "line-through", color: "#999" }
          : undefined
      }
      title={displaySubject}
    >
      {displaySubject}
    </span>
  );

  return (
    <>
      <tr className={styles.row}>
        <td className={styles.td} style={{ width: 36 }} />
        <td className={styles.td}>
          {/* Wraps: the thread name plus the "Show extracted content …" and
              "N attachments" controls do not fit one line on a narrow screen,
              so they stack instead of overflowing the row. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: 4,
            }}
          >
            <span style={{ marginRight: 6 }}>✉️</span>
            {isOutOfDate && <OutOfDateIcon position="left" tooltip="Marked out of date" />}
            {nameNode}
            {!isTombstone && <PossibleOtherSiteBadge uploadId={entry.effective_upload_id} />}
            {!isTombstone && (
              <ExtractionStatusButton
                uploadId={entry.upload_id}
                isExpanded={expanded}
                onToggle={onToggleExpand}
                chars={entry.extracted_text_chars}
                pages={entry.total_pages}
                heldUntil={entry.extraction_not_before}
              />
            )}
            {!isTombstone && (
              <EmailThreadAttachmentsToggle
                uploadId={entry.upload_id}
                expanded={attachmentsExpanded}
                onToggle={onToggleAttachmentsExpanded}
              />
            )}
            {isTombstone && reasonText && (
              <span
                style={{
                  marginLeft: 8,
                  color: "#b03030",
                  fontSize: 11,
                  fontStyle: "italic",
                }}
                title={reasonText}
              >
                · {reasonText}
              </span>
            )}
          </div>
          {/* Where this thread was seen, through the ONE shared renderer - the
              Gmail tree had no provenance at all, so a thread ingested twice (or
              an attachment that arrived on several messages) showed nothing.
              Collapsed past one place, same as everywhere else. */}
          {dedupeRenderedSightings(entry.sightings ?? []).length > 1 && (
            <div style={{ paddingLeft: 26, marginTop: 2 }}>
              <SightingList sightings={entry.sightings} />
            </div>
          )}
        </td>
        <td className={styles.tdSmall}>
          {/* Gmail-sync rows are always ``email_thread``; the column does
              double duty as a thread summary - "3 messages · 5 attachments" -
              via EmailThreadTypeCell. The count comes from the upload row's own
              ``gmail_message_count``, written by the gmail-sync writer, which is
              the one signal a TOMBSTONE still carries - so a removed thread keeps
              the audit signal an operator reviewing it relies on. */}
          {isTombstone ? (
            <span style={{ color: "#aaa" }}>
              {entry.gmail_message_count !== null &&
              entry.gmail_message_count !== undefined
                ? `${entry.gmail_message_count} message${entry.gmail_message_count === 1 ? "" : "s"}`
                : "email thread"}
            </span>
          ) : (
            <span
              style={{
                display: "inline-flex",
                gap: 8,
                alignItems: "baseline",
                flexWrap: "wrap",
              }}
            >
              <EmailThreadTypeCell
                uploadId={entry.upload_id}
                messageCount={entry.gmail_message_count ?? null}
              />
              <ReextractLink
                projectId={projectId}
                uploadId={entry.upload_id}
                extractionStatus={entry.extraction_status}
              />
            </span>
          )}
        </td>
        <td className={styles.tdSmall} style={{ color: "#6b7280" }}>
          {isTombstone ? (
            <span title={`Removed ${relativeTime(entry.removed_at)}`}>removed</span>
          ) : (
            (() => {
              const iso = entryLastMessageIso(entry);
              const fellBack =
                entry.gmail_last_message_at == null && entry.last_pulled_at != null;
              return (
                <span
                  title={
                    iso
                      ? `${fellBack ? "Last synced" : "Last message"}: ${new Date(
                          iso,
                        ).toLocaleString()}`
                      : undefined
                  }
                >
                  {relativeTime(iso)}
                </span>
              );
            })()
          )}
        </td>
        <td
          className={styles.tdSmall}
          style={{ whiteSpace: "nowrap", textAlign: "right" }}
        >
          {!isTombstone && (
            <button
              type="button"
              className={styles.openBtn}
              onClick={() =>
                setFreshness.mutate({
                  project_id: projectId,
                  upload_id: entry.upload_id,
                  out_of_date: !isOutOfDate,
                })
              }
              disabled={setFreshness.isLoading}
              title={
                isOutOfDate
                  ? "Marked out of date. Click to mark fresh."
                  : "Mark this thread as out of date"
              }
            >
              {isOutOfDate ? "Mark fresh" : "Mark out of date"}
            </button>
          )}
          {!isTombstone && entry.gmail_thread_id && (
            <button
              type="button"
              className={styles.deleteBtn}
              style={{ marginLeft: 6 }}
              onClick={() => {
                if (
                  !confirm(
                    "Exclude this thread? It will be tombstoned and never filed onto this project again. Use Dismiss on the tombstone to hide it from view.",
                  )
                )
                  return;
                excludeThread.mutate(
                  {
                    project_id: projectId,
                    thread_id: entry.gmail_thread_id ?? "",
                  },
                  {
                    // No silent mutation failure.
                    onError: (e: unknown) =>
                      window.alert(
                        `Couldn't exclude this thread: ${e instanceof Error ? e.message : String(e)}`,
                      ),
                  },
                );
              }}
              disabled={excludeThread.isLoading}
              title="Tombstone this thread's row and stop the sweep filing it here. Mirrors Drive's folder-exclude on already-pulled files."
            >
              Exclude
            </button>
          )}
          {isTombstone &&
            entry.removal_reason === "operator_excluded" &&
            entry.gmail_thread_id && (
              <button
                type="button"
                className={styles.openBtn}
                style={{ marginRight: 6 }}
                onClick={() =>
                  reinstate.mutate(
                    {
                      project_id: projectId,
                      thread_id: entry.gmail_thread_id ?? "",
                    },
                    {
                      onError: (e: unknown) =>
                        window.alert(
                          `Couldn't reinstate this thread: ${e instanceof Error ? e.message : String(e)}`,
                        ),
                    },
                  )
                }
                disabled={reinstate.isLoading}
                title="Bring this thread back into play: its data re-contributes to the project. Mirrors Drive's per-file Reinstate."
              >
                {reinstate.isLoading ? "Reinstating…" : "Reinstate"}
              </button>
            )}
          {isTombstone && (
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() =>
                dismiss.mutate(
                  {
                    upload_id: entry.upload_id,
                    project_id: projectId,
                  },
                  {
                    onError: (e: unknown) =>
                      window.alert(
                        `Couldn't dismiss this row: ${e instanceof Error ? e.message : String(e)}`,
                      ),
                  },
                )
              }
              disabled={dismiss.isLoading}
              title="Hide this row from the default view"
            >
              Dismiss
            </button>
          )}
        </td>
      </tr>
      {!isTombstone && attachmentsExpanded && (
        <EmailThreadAttachmentsRows
          uploadId={entry.upload_id}
          projectId={projectId}
          colSpan={5}
          nestedAttachments={entry.nested_attachments ?? []}
        />
      )}
      {expanded && (
        <tr>
          <td />
          <td colSpan={4} style={{ padding: "8px 16px", background: "#fafafa" }}>
            <ExtractedContentPanel
              uploadId={entry.upload_id}
              chars={entry.extracted_text_chars}
              pages={entry.total_pages}
            />
          </td>
        </tr>
      )}
    </>
  );
};
